/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { SubscriptionDto } from '@/commons/dtos';
import SubscriptionCardMenuButton from '@/components/SubscriptionCardMenuButton';
import SubscriptionDonutChart from '@/components/SubscriptionDonutChart';
import { useTranslations } from '@/hooks/useTranslations';
import useVideoDialog from '@/hooks/useVideoDialog';
import { Button, Card, FlexBox, Label } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SubscriptionCard({ sub, onNavigate }: { sub: SubscriptionDto; onNavigate: (sub: SubscriptionDto) => void }) {
    const { answerCount, questionCount, launchTriggered, runId, productDto } = sub;
    const skuId = productDto.sku;
    const navigate = useNavigate();
    const { t } = useTranslations();

    const status = useMemo(() => {
        if (!runId) return 'NOT_ACTIVATED';
        if (launchTriggered && !!runId && skuId === 1) return 'INTEGRATION_ACTIVATED';
        if (launchTriggered && !!runId) return 'ACTIVATED';
        return 'ACTIVATION_IN_PROGRESS';
    }, [runId, launchTriggered]);

    const answerPercentage = useMemo(() => {
        if (status === 'NOT_ACTIVATED') return 0;
        return Math.floor((answerCount / questionCount) * 100);
    }, [answerCount, questionCount, status]);

    const labelTextKey = {
        ACTIVATED: 'MONITOR_BUTTON_LANDING',
        ACTIVATION_IN_PROGRESS: 'RESUME_SET_UP',
        INTEGRATION_ACTIVATED: 'RESUME_SET_UP',
        NOT_ACTIVATED: 'SET_UP',
    }[status];

    const toggleLearnMore = useVideoDialog({
        title: productDto.name,
        src: productDto.videoUrl,
    });

    return (
        <Card data-sap-ui-fastnavgroup="true" title={sub.productDto.name} style={{ width: 240 }}>
            <FlexBox alignItems="Center" justifyContent="Center" style={{ height: 76 }}>
                <Label
                    wrappingType="Normal"
                    style={{
                        color: ThemingParameters.sapTile_TitleTextColor,
                        fontWeight: 700,
                        textAlign: 'center',
                    }}
                >
                    {sub.productDto.name} ({sub.productDto.sku})
                </Label>
            </FlexBox>
            <SubscriptionDonutChart answerPercentage={answerPercentage} launchTriggered={launchTriggered} />

            <FlexBox
                alignItems="Center"
                justifyContent="Center"
                style={{
                    gap: ThemingParameters.sapShell_Gap_S,
                    marginTop: ThemingParameters.sapContent_Margin_Medium,
                    marginBottom: ThemingParameters.sapContent_Margin_Small,
                }}
            >
                <Button
                    style={{ maxWidth: 120 }}
                    data-testid={`best-practice-suite-scenario-navigate-button-${sub.productDto.name}`}
                    tooltip={t(labelTextKey)}
                    onClick={() => {
                        if (status === 'ACTIVATED') {
                            navigate(`/monitor/${sub.flowId}`);
                        } else {
                            onNavigate(sub);
                        }
                    }}
                >
                    {t(labelTextKey)}
                </Button>

                {status === 'NOT_ACTIVATED' && (
                    <Button style={{ border: 'none', maxWidth: 120 }} onClick={toggleLearnMore}>
                        {t('LEARN_MORE')}
                    </Button>
                )}

                {status === 'ACTIVATED' && <SubscriptionCardMenuButton flowId={sub.flowId} runId={sub.runId!} />}

                {status === 'INTEGRATION_ACTIVATED' && (
                    <Button
                        style={{ maxWidth: 120 }}
                        onClick={() => {
                            navigate(`/monitor/${sub.flowId}`);
                        }}
                    >
                        {t('MONITOR_BUTTON_LANDING')}
                    </Button>
                )}
            </FlexBox>
        </Card>
    );
}
